import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export const ProductCard = ({ product, index = 0, price = null }) => {
  const { t } = useLanguage();
  const hasDetail = Boolean(product.slug);

  const ImageInner = (
    <>
      <img
        src={product.image}
        alt={product.name}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </>
  );

  let cta;
  if (price !== null) {
    cta = (
      <div className="flex items-center justify-between pt-2">
        <span className="font-bebas text-2xl text-[#0D0D0D]">${price}</span>
        <button
          className="bg-[#CE2B37] text-white font-bebas text-sm tracking-wider px-4 py-2 hover:bg-[#a82230] transition-colors duration-200"
          data-testid={`add-to-cart-${product.id}`}
        >
          Add to Cart
        </button>
      </div>
    );
  } else if (hasDetail) {
    cta = (
      <Link
        to={`/product/${product.slug}`}
        className="block w-full text-center bg-[#CE2B37] text-white font-bebas text-sm tracking-wider py-2.5 hover:bg-[#a82230] transition-colors duration-200"
        data-testid={`view-details-${product.id}`}
      >
        {t.featured.requestQuote}
      </Link>
    );
  } else {
    cta = (
      <button
        className="w-full bg-[#CE2B37] text-white font-bebas text-sm tracking-wider py-2.5 hover:bg-[#a82230] transition-colors duration-200"
        data-testid={`request-quote-${product.id}`}
      >
        {t.featured.requestQuote}
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group bg-white border border-[#E8E8E8] hover:border-[#CE2B37] transition-all duration-300 hover:-translate-y-1"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      {hasDetail ? (
        <Link
          to={`/product/${product.slug}`}
          className="relative block aspect-square overflow-hidden bg-[#FAFAFA]"
          data-testid={`product-image-link-${product.id}`}
        >
          {ImageInner}
        </Link>
      ) : (
        <div className="relative aspect-square overflow-hidden bg-[#FAFAFA]">
          {ImageInner}
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-3">
        <div>
          {hasDetail ? (
            <Link to={`/product/${product.slug}`} data-testid={`product-title-link-${product.id}`}>
              <h3 className="font-bebas text-lg text-[#0D0D0D] tracking-wide hover:text-[#CE2B37] transition-colors leading-tight">
                {product.name}
              </h3>
            </Link>
          ) : (
            <h3 className="font-bebas text-lg text-[#0D0D0D] tracking-wide leading-tight">
              {product.name}
            </h3>
          )}
          {product.model && (
            <p className="font-dm text-[11px] text-[#6B6B6B] mt-1">
              Model: {product.model}
            </p>
          )}
        </div>

        <p className="font-dm text-xs text-[#6B6B6B] leading-relaxed line-clamp-2">
          {product.description}
        </p>

        {/* Color Swatches */}
        {product.swatches && product.swatches.length > 0 && (
          <div className="flex items-center gap-2">
            {product.swatches.map((color, i) => (
              <span
                key={`${color}-${i}`}
                className="w-4 h-4 rounded-full border border-[#E8E8E8]"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        )}

        {/* Price or Quote Button */}
        {cta}
      </div>
    </motion.div>
  );
};
